import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  CommandDashboardKeys,
  getRecentAdminActions,
} from '../../api/command-dashboard-api';
import { Messages } from '../../utils/message-constants';

export const RECENT_ADMIN_ACTIONS_PAGE_SIZE = 5;

export function useRecentAdminActionsTile(enabled = true) {
  const [page, setPage] = useState(0);

  const [cursors, setCursors] = useState<Record<number, string | undefined>>({
    0: undefined,
  });
  const [sortDirection, setSortDirection] = useState<
    'asc' | 'desc' | undefined
  >(undefined);

  const query = useQuery({
    queryKey: [
      CommandDashboardKeys.RecentAdminActionsTile,
      page,
      cursors[page],
      sortDirection,
    ],
    queryFn: () => getRecentAdminActions(cursors[page], sortDirection),
    enabled,
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.CommandDashboard.RecentAdminActionsTileLoadError,
    },
  });

  const onPageChange = (event: unknown, newPage: number) => {
    if (event && typeof event === 'object' && 'stopPropagation' in event) {
      (event as { stopPropagation: () => void }).stopPropagation();
    }

    if (query.isFetching) {
      return;
    }
    if (newPage > page && query.data?.nextCursor) {
      setCursors((prev) => ({ ...prev, [newPage]: query.data!.nextCursor! }));
    }
    setPage(newPage);
  };

  const toggleSort = () => {
    setSortDirection((prev) =>
      prev === undefined ? 'asc' : prev === 'asc' ? 'desc' : undefined
    );
    setPage(0);
    setCursors({ 0: undefined });
  };

  return {
    entries: query.data?.entries ?? [],
    totalCount: query.data?.totalCount ?? 0,
    todayCount: query.data?.todayCount ?? 0,
    yesterdayCount: query.data?.yesterdayCount ?? 0,
    hasLoadedData: query.data !== undefined,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    page,
    onPageChange,
    sortDirection,
    toggleSort,
  };
}
